import { HttpErrorResponse } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import { Observable, catchError, expand, map, of, reduce, throwError } from 'rxjs'
import { ChangeUsersDetailsResponseDTO } from '../dto/edit-users/change-users-details-response.dto'
import { EditUsersFailedError } from '../errors/edit-users-failed.error'
import { ListUsersQueryDTO } from '../dto/list-users/list-users-query.dto'
import { GetUserByEmailQueryResponseDTO, ListUsersQueryResponseDTO } from '../dto/list-users/list-users-query-response.dto'
import { PlatformAccessService } from '@app/common/services/platform-access.service'
import { CreateUserDTO } from '../dto/create-users/create-user.dto'
import { CreateUsersResponseDTO } from '../dto/create-users/create-users-response.dto'
import { CreateUsersRequestDTO } from '../dto/create-users/create-users-request.dto'
import { ArchiveUsersRequestDTO } from '../dto/archive-users/archive-users'
import { User } from '../types/user'
import { EditUserDTO } from '../dto/edit-users/edit-user.dto'
import { EditUsersRequestDTO } from '../dto/edit-users/edit-users-request.dto'
import { UnarchiveUsersRequestDTO } from '../dto/archive-users/unarchive-users'
import { UserActivityResponseDTO } from '../dto/user-activity/user-activity-response.dto'
import { UserActivityQueryDTO } from '../dto/user-activity/user-activity-query.dto'
import { RouterAdapterService } from '@app/common/router/router-adapter.service'
import { NoAvailableLicensesError } from '../errors/no-available-licenses-error'
import { UserServiceApiErrorPayload } from '../types/user-service-api-error-payload'
import { UUIDv4 } from '@app/common/types/uuid'
import { MoveUsersRequestDTO } from '../dto/move-users/move-users-request.dto'
import { MoveUsersResponseDTO } from '../dto/move-users/move-users-response.dto'
import { BillingPlatform } from '@app/organization-management/types/billing-platform'
import { OrderType } from '@app/organization-management/types/order-type'
import { isActivePilot } from '@app/common/util/is-active-pilot'

export const NO_AVAILABLE_LICENSES_ERROR_CODE = 'user-assign-licenses-not-enough-licenses'

/**
 * Sentinel passed in place of an organization UUID to request a system-wide
 * (cross-tenant) users list. Today this is interpolated directly into the
 * path: GET /organizations/*\/users — backend support for this shape needs
 * confirmation.
 */
export const ALL_ORGS_SENTINEL = '*'

export interface MoveUsersParams {
  userIds: UUIDv4[]
  targetOrganizationId: UUIDv4
  licenseTypePlanId?: UUIDv4
  licensePoolId?: UUIDv4
}

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private readonly platformAccess: PlatformAccessService = inject(PlatformAccessService)
  private readonly routerAdapter = inject(RouterAdapterService)

  createUsers(users: CreateUserDTO[], organizationId?: string): Observable<CreateUsersResponseDTO> {
    const createUsersRequest = new CreateUsersRequestDTO(users)
    const orgId = organizationId || this.routerAdapter.routeOrganizationId()

    return this.platformAccess
      .post<CreateUsersResponseDTO>(`/organizations/${orgId}/users`, createUsersRequest)
      .pipe(catchError(error => this.handleLicenseAvailabilityError(error)))
  }

  moveUsers({ userIds, targetOrganizationId, licenseTypePlanId, licensePoolId }: MoveUsersParams): Observable<MoveUsersResponseDTO> {
    const body = new MoveUsersRequestDTO(userIds, licenseTypePlanId, licensePoolId)

    return this.platformAccess.post<MoveUsersResponseDTO>(`/organizations/${targetOrganizationId}/users/move`, body)
  }

  editUsers(users: EditUserDTO[], organizationId?: string): Observable<ChangeUsersDetailsResponseDTO> {
    const editUsersRequest = new EditUsersRequestDTO(users)
    const orgId = this.resolveOrganizationId(organizationId)
    return this.platformAccess
      .post<ChangeUsersDetailsResponseDTO>(`/organizations/${orgId}/users/details-changes`, editUsersRequest)
      .pipe(
        map(result => this.ensureEditUsersSucceeded(result)),
        catchError(error => this.handleLicenseAvailabilityError(error))
      )
  }

  updateUserEmail(userId: string, email: string, organizationId?: string): Observable<void> {
    const orgId = this.resolveOrganizationId(organizationId)
    return this.platformAccess.post<void>(`/organizations/${orgId}/users/${userId}/change-email`, { email })
  }

  listUsers(query?: ListUsersQueryDTO, organizationId?: string): Observable<ListUsersQueryResponseDTO> {
    const orgId = organizationId || this.routerAdapter.routeOrganizationId()
    // When orgId === ALL_ORGS_SENTINEL ('*'), the resulting URL is
    // /organizations/*/users — this is the system-wide list path used by the
    // /admin/users/all page.
    return (this.platformAccess.get(`/organizations/${orgId}/users`, query) as Observable<ListUsersQueryResponseDTO>).pipe(
      map(response => ({ ...response, users: response.users.map(u => this.hydrateUser(u)) }))
    )
  }

  listAllUsers(query: ListUsersQueryDTO, organizationId?: string): Observable<User[]> {
    query.page = 1
    query.pageSize = 100
    return this.listUsers(query, organizationId).pipe(
      expand(response => {
        if (response.page < response.totalPages) {
          return this.listUsers({ ...query, page: response.page + 1 }, organizationId)
        }
        return []
      }),
      map(response => response.users),
      reduce((acc: User[], users: User[]) => acc.concat(users), [])
    )
  }

  getUserById(id: string, organizationId = '11111111-1111-1111-1111-111111111111'): Observable<User | undefined> {
    const orgId = this.routerAdapter.routeOrganizationId() || organizationId
    return (this.platformAccess.get(`/organizations/${orgId}/users/${id}`) as Observable<ListUsersQueryResponseDTO>).pipe(
      map((response: ListUsersQueryResponseDTO) => {
        const rawUser = response.users?.[0]
        return rawUser ? this.hydrateUser(rawUser) : undefined
      })
    )
  }

  getUserByEmail(email: string, organizationId = '11111111-1111-1111-1111-111111111111'): Observable<User | null> {
    const queryParams = {
      email: email,
    }
    const orgId = this.routerAdapter.routeOrganizationId() || organizationId
    return (this.platformAccess.get(`/organizations/${orgId}/users/by-email`, queryParams) as Observable<GetUserByEmailQueryResponseDTO>).pipe(
      map((response: GetUserByEmailQueryResponseDTO) => {
        const user = response.users[0]
        return user ? this.hydrateUser(user) : null
      }),
      catchError(err => {
        if (err instanceof HttpErrorResponse && err.status === 404) {
          return of(null)
        }
        return throwError(() => err)
      })
    )
  }

  archiveUsers(userIds: string[], organizationId?: string) {
    const archiveUsersRequest = new ArchiveUsersRequestDTO(userIds)
    const orgId = this.resolveOrganizationId(organizationId)
    return this.platformAccess.put(`/organizations/${orgId}/users/archive`, archiveUsersRequest)
  }

  unarchiveUsers(userIds: string[], organizationId?: string) {
    const archiveUsersRequest = new UnarchiveUsersRequestDTO(userIds)
    const orgId = this.resolveOrganizationId(organizationId)
    return this.platformAccess.put(`/organizations/${orgId}/users/unarchive`, archiveUsersRequest)
  }

  getUserActivityData(query: UserActivityQueryDTO): Observable<UserActivityResponseDTO | undefined> {
    const orgId = this.routerAdapter.routeOrganizationId()
    return this.platformAccess.get(`/organizations/${orgId}/users/${query.userId}/activity`, {
      sortDirection: query.sortDirection,
    }) as Observable<UserActivityResponseDTO | undefined>
  }

  /**
   * Derives the customer-facing order type for a user based on their
   * organization's billing platform and (for Chargify) the customer's
   * `orderType` field. Mirrors the logic previously inlined in
   * `ExistingUserInfoCardComponent` so any component can rely on a single
   * source of truth via `user.orderType`.
   */
  getOrderType(user: User): OrderType {
    const billingPlatform = user.organization?.billingPlatform
    if (!billingPlatform) {
      return OrderType.NOT_AVAILABLE
    }

    if (billingPlatform === BillingPlatform.MAXIO) {
      return OrderType.REMITTANCE
    }

    const customerOrderType = user.organization?.customer?.orderType
    if (!customerOrderType) {
      return OrderType.NOT_AVAILABLE
    }
    return customerOrderType.toLowerCase() === 'web' ? OrderType.AUTOMATIC : OrderType.REMITTANCE
  }

  private resolveOrganizationId(organizationId?: string): string {
    const orgId = organizationId ?? this.routerAdapter.routeOrganizationId()
    if (!orgId) throw new Error('Organization id is required for user mutation requests')
    return orgId
  }

  private hydrateUser(rawUser: User): User {
    const user = Object.assign(new User(), rawUser)
    user.orderType = this.getOrderType(user)
    user.belongsToActivePilot = isActivePilot(user)
    return user
  }

  private ensureEditUsersSucceeded(result: ChangeUsersDetailsResponseDTO | null): ChangeUsersDetailsResponseDTO {
    if (!result?.failedUsers || !result?.updatedUsers) {
      throw new Error('Unexpected empty response from user details update')
    }
    if (result.failedUsers.length > 0) {
      throw new EditUsersFailedError(result.failedUsers)
    }
    return result
  }

  private handleLicenseAvailabilityError(error: unknown) {
    if (error instanceof HttpErrorResponse && error.status === 400) {
      const payload = error.error as UserServiceApiErrorPayload | null

      if (payload?.code === NO_AVAILABLE_LICENSES_ERROR_CODE) {
        return throwError(() => new NoAvailableLicensesError(payload.code as string, payload.errorMessage))
      }
    }

    return throwError(() => error)
  }
}
